import {
  Box,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import Cookies from 'js-cookie';
import moment from 'moment';
import { useEffect, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { api } from '../../shared/services/api';
import type { IResponseAjud } from '../../shared/services/types/response';
import LayoutBase from '../../shared/layouts';

// --- Constantes compartilhadas ---

const statusLabels: Record<string, string> = {
  CREATED: 'Criado',
  PENDING: 'Pendente',
  DONE: 'Deferido',
  CANCEL: 'Cancelado',
  REFUSED: 'Recusado',
  ERROR: 'Erro',
  READY_TO_PROCESS: 'Pronto p/ Processar',
  AWAITING_MANUAL_ACCEPTANCE: 'Aguardando Aceite',
};

const STATUS_COLORS: Record<string, string> = {
  CREATED: '#90a4ae',
  PENDING: '#ffa726',
  DONE: '#66bb6a',
  CANCEL: '#bdbdbd',
  REFUSED: '#ef5350',
  ERROR: '#f44336',
  READY_TO_PROCESS: '#42a5f5',
  AWAITING_MANUAL_ACCEPTANCE: '#ab47bc',
};

const typeRecordLabels: Record<string, string> = {
  '03': 'Bloqueio',
  '05': 'Desbloqueio',
  '07': 'Transferência',
};

const TYPE_COLORS = ['#115C5b', '#C6A76E', '#42a5f5'];

const parseDate = (dateStr: string | null | undefined): string | null => {
  if (!dateStr) return null;
  const digitsOnly = dateStr.replace(/\D/g, '');
  if (digitsOnly.length === 8) {
    const parsed = moment(digitsOnly, 'DDMMYYYY');
    if (parsed.isValid()) return parsed.format('DD/MM/YYYY');
  }
  const parsed = moment(dateStr);
  if (parsed.isValid()) return parsed.format('DD/MM/YYYY');
  return null;
};

// --- Componentes auxiliares ---

interface SummaryCardProps {
  title: string;
  count: number;
  color: string;
  icon: React.ReactNode;
}

const SummaryCard = ({ title, count, color, icon }: SummaryCardProps) => (
  <Paper
    sx={{
      flex: 1,
      minWidth: 160,
      p: 2.5,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      borderLeft: `4px solid ${color}`,
    }}
  >
    <Box sx={{ color, display: 'flex' }}>{icon}</Box>
    <Box>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {title}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
        {count}
      </Typography>
    </Box>
  </Paper>
);

interface GenericRow {
  status: string;
  [key: string]: unknown;
}

interface DashboardChartsProps {
  rows: GenericRow[];
  totalItems: number;
  dateField: string;
  dateLabel: string;
  primaryColor: string;
  typeField?: string;
  typeLabels?: Record<string, string>;
}

const DashboardCharts = ({
  rows,
  totalItems,
  dateField,
  dateLabel,
  primaryColor,
  typeField,
  typeLabels,
}: DashboardChartsProps) => {
  const [statusTab, setStatusTab] = useState(0);

  const countByStatus = (status: string) =>
    rows.filter((r) => r.status === status).length;

  const today = moment().format('DD/MM/YYYY');

  const todayRows = rows.filter((r) => {
    const raw = r[dateField] as string | null | undefined;
    return parseDate(raw) === today;
  });

  // Registros por dia
  const dailyData = (() => {
    const map: Record<string, number> = {};
    rows.forEach((r) => {
      const raw = r[dateField] as string | null | undefined;
      const date = parseDate(raw);
      if (date) {
        map[date] = (map[date] || 0) + 1;
      }
    });
    return Object.entries(map)
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => moment(a.date, 'DD/MM/YYYY').valueOf() - moment(b.date, 'DD/MM/YYYY').valueOf())
      .slice(-15);
  })();

  // Distribuição por status
  const buildStatusData = (source: GenericRow[]) => {
    const map: Record<string, number> = {};
    source.forEach((r) => {
      map[r.status] = (map[r.status] || 0) + 1;
    });
    return Object.entries(map).map(([status, count]) => ({
      name: statusLabels[status] || status,
      value: count,
      key: status,
    }));
  };

  const statusData = statusTab === 0 ? buildStatusData(rows) : buildStatusData(todayRows);

  // Distribuição por tipo (opcional)
  const typeData = (() => {
    if (!typeField) return [];
    const map: Record<string, number> = {};
    rows.forEach((r) => {
      const key = r[typeField] as string;
      if (key) map[key] = (map[key] || 0) + 1;
    });
    return Object.entries(map).map(([type, count]) => ({
      name: typeLabels?.[type] || type,
      count,
    }));
  })();

  return (
    <Box>
      {/* Cards de resumo */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <SummaryCard
          title="Total"
          count={totalItems}
          color={primaryColor}
          icon={<AssignmentIcon fontSize="large" />}
        />
        <SummaryCard
          title="Pendentes"
          count={countByStatus('PENDING')}
          color="#ffa726"
          icon={<HourglassEmptyIcon fontSize="large" />}
        />
        <SummaryCard
          title="Deferidos"
          count={countByStatus('DONE')}
          color="#66bb6a"
          icon={<CheckCircleIcon fontSize="large" />}
        />
        <SummaryCard
          title="Erros"
          count={countByStatus('ERROR')}
          color="#f44336"
          icon={<ErrorIcon fontSize="large" />}
        />
      </Box>

      {/* Gráficos - linha 1 */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Paper sx={{ flex: 2, minWidth: 400, p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
            {dateLabel}
          </Typography>
          {dailyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" fontSize={12} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" name="Registros" fill={primaryColor} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 8 }}>
              Nenhum dado disponível
            </Typography>
          )}
        </Paper>

        <Paper sx={{ flex: 1, minWidth: 300, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              Distribuição por Status
            </Typography>
            <Tabs
              value={statusTab}
              onChange={(_, v) => setStatusTab(v)}
              sx={{
                minHeight: 32,
                '& .MuiTab-root': { minHeight: 32, py: 0, px: 1.5, fontSize: 12, textTransform: 'none' },
              }}
            >
              <Tab label="Tudo" />
              <Tab label="Hoje" />
            </Tabs>
          </Box>
          {statusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                  fontSize={11}
                >
                  {statusData.map((entry) => (
                    <Cell key={entry.key} fill={STATUS_COLORS[entry.key] || '#90a4ae'} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 8 }}>
              Nenhum dado disponível
            </Typography>
          )}
        </Paper>
      </Box>

      {/* Gráfico por tipo (opcional) */}
      {typeData.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Distribuição por Tipo de Registro
          </Typography>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={typeData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={120} fontSize={13} />
              <Tooltip />
              <Bar dataKey="count" name="Quantidade" radius={[0, 4, 4, 0]}>
                {typeData.map((_, idx) => (
                  <Cell key={idx} fill={TYPE_COLORS[idx % TYPE_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}
    </Box>
  );
};

// --- Componente principal ---

const Home = () => {
  const theme = useTheme();
  const token = Cookies.get('@token');
  const [tabIndex, setTabIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // AJUD
  const [ajudRows, setAjudRows] = useState<IResponseAjud[]>([]);
  const [ajudTotal, setAjudTotal] = useState(0);

  // CCS0001
  const [ccs0001Rows, setCcs0001Rows] = useState<GenericRow[]>([]);
  const [ccs0001Total, setCcs0001Total] = useState(0);

  // CCS0011
  const [ccs0011Rows, setCcs0011Rows] = useState<GenericRow[]>([]);
  const [ccs0011Total, setCcs0011Total] = useState(0);

  const headers = {
    Authorization: 'Bearer ' + token,
    'Content-type': 'application/json',
  };

  useEffect(() => {
    fetchAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    setIsLoading(true);
    try {
      const [ajudRes, ccs0001Res, ccs0011Res] = await Promise.allSettled([
        api.get('/ajudArchive/getAll', { headers, params: { limit: 9999, page: 1 } }),
        api.get('/detailPerson/getAll', { headers, params: { limit: 9999, page: 1 } }),
        api.get('/reportFinancial/getAll', { headers, params: { limit: 9999, page: 1 } }),
      ]);

      if (ajudRes.status === 'fulfilled') {
        const d = ajudRes.value.data;
        setAjudRows(d?.data || []);
        setAjudTotal(d?.meta?.totalItems || 0);
      }
      if (ccs0001Res.status === 'fulfilled') {
        const d = ccs0001Res.value.data;
        setCcs0001Rows(d?.data || []);
        setCcs0001Total(d?.meta?.totalItems || 0);
      }
      if (ccs0011Res.status === 'fulfilled') {
        const d = ccs0011Res.value.data;
        setCcs0011Rows(d?.data || []);
        setCcs0011Total(d?.meta?.totalItems || 0);
      }
    } catch {
      // silencioso
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <LayoutBase>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <CircularProgress />
        </Box>
      </LayoutBase>
    );
  }

  const allRows: GenericRow[] = [
    ...ajudRows.map((r) => ({ ...r, _source: 'AJUD' })),
    ...ccs0001Rows.map((r) => ({ ...r, _source: 'CCS0001' })),
    ...ccs0011Rows.map((r) => ({ ...r, _source: 'CCS0011' })),
  ];
  const allTotal = ajudTotal + ccs0001Total + ccs0011Total;

  return (
    <LayoutBase>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          sx={{
            '& .MuiTab-root': { fontWeight: 'bold', textTransform: 'none' },
            '& .Mui-selected': { color: theme.palette.primary.main },
          }}
        >
          <Tab label={`Geral (${allTotal})`} />
          <Tab label={`AJUD (${ajudTotal})`} />
          <Tab label={`CCS0001 (${ccs0001Total})`} />
          <Tab label={`CCS0011 (${ccs0011Total})`} />
        </Tabs>
      </Box>

      {/* Tab Geral */}
      {tabIndex === 0 && (
        <DashboardCharts
          rows={allRows}
          totalItems={allTotal}
          dateField="created_at"
          dateLabel="Registros por Data de Criação"
          primaryColor={theme.palette.primary.main}
        />
      )}

      {/* Tab AJUD */}
      {tabIndex === 1 && (
        <DashboardCharts
          rows={ajudRows}
          totalItems={ajudTotal}
          dateField="shipping_date"
          dateLabel="Registros por Data de Envio"
          primaryColor={theme.palette.primary.main}
          typeField="type_record"
          typeLabels={typeRecordLabels}
        />
      )}

      {/* Tab CCS0001 */}
      {tabIndex === 2 && (
        <DashboardCharts
          rows={ccs0001Rows}
          totalItems={ccs0001Total}
          dateField="created_at"
          dateLabel="Registros por Data de Criação"
          primaryColor="#C6A76E"
        />
      )}

      {/* Tab CCS0011 */}
      {tabIndex === 3 && (
        <DashboardCharts
          rows={ccs0011Rows}
          totalItems={ccs0011Total}
          dateField="created_at"
          dateLabel="Registros por Data de Criação"
          primaryColor="#42a5f5"
        />
      )}
    </LayoutBase>
  );
};

export default Home;
